import React, { useState } from 'react';
import { Contact } from '../../../types/contact.types';
import { FaArrowUp } from 'react-icons/fa';
import { Loader2, PlusCircle } from 'lucide-react';
import { useLeadScoring } from '../../../api/leadScore';
import { useToast } from '@crisellevaldez/speculo-ui';

interface LeadScoreDisplayProps {
  contact: Contact;
  size?: 'sm' | 'normal'; // Size variant of the component
  onScoreCalculated?: (updatedContact: Contact) => void; // Callback to update contact data with new score
}

export const LeadScoreDisplay: React.FC<LeadScoreDisplayProps> = ({
  contact,
  size = 'normal', // Default to normal size
  onScoreCalculated,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const { scoreSelectedContact } = useLeadScoring();
  const { showToast } = useToast();

  // Function to trigger lead score calculation
  const handleCalculateScore = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isCalculating) return;

    try {
      setIsCalculating(true);
      const result = await scoreSelectedContact(contact.id, true);
      console.log('LeadScoreDisplay - API Response:', result);
      showToast({
        message: 'Lead score calculated successfully',
        variant: 'success',
      });

      // Update the contact with the new score information
      if (onScoreCalculated && result && result.data) {
        const updatedContact = {
          ...contact,
          score: result.data.score,
          previous_score: contact.score || 0,
        };
        console.log('LeadScoreDisplay - Updated Contact:', updatedContact);
        onScoreCalculated(updatedContact);
      }
    } catch (error) {
      console.error('Error calculating lead score:', error);
      showToast({
        message: 'Failed to calculate lead score',
        variant: 'error',
      });
    } finally {
      setIsCalculating(false);
    }
  };

  // Get the score from the contact, default to 0 if not present
  // The score is expected to be a number between 0 and 100
  // Cap the score at 99 if it's greater than 99
  const score = typeof contact.score === 'number' ? Math.min(contact.score, 99) : 0;

  // Determine color based on score
  const getScoreColor = (score: number) => {
    if (score >= 50) return 'text-green-600';
    if (score >= 25) return 'text-amber-500';
    return 'text-red-500';
  };

  const scoreColor = getScoreColor(score);
  const textSize = size === 'sm' ? 'text-xs' : 'text-base';

  // Get the previous score, capped at 99 if it's greater than 99
  const previousScore =
    typeof contact.previous_score === 'number' ? Math.min(contact.previous_score, 99) : 0;

  // Check if score has increased compared to previous score
  const hasIncreased =
    typeof contact.previous_score === 'number' &&
    typeof score === 'number' &&
    score > previousScore;

  // Format the hover text
  const getHoverText = () => {
    if (!score) return 'Click to calculate lead score';

    if (hasIncreased) {
      return `Lead Score improved from ${previousScore}%. Click to recalculate.`;
    }

    return `Current lead score: ${score}%. Click to recalculate.`;
  };

  return (
    <div className="relative">
      {/* Base display */}
      <div
        className={`font-semibold flex items-center gap-1 cursor-pointer transition-all duration-300 ease-in-out`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCalculateScore}
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {isCalculating ? (
          <div className="flex items-center">
            <Loader2
              className="w-3 h-3 text-blue-600 mr-1"
              style={{ animation: 'spin 1s linear infinite' }}
            />
            <span className="text-blue-800 text-sm">Calculating...</span>
          </div>
        ) : (
          <>
            {score > 0 ? (
              <div className="flex items-center justify-center">
                <span className={`${scoreColor} ${textSize} font-bold`}>{score}%</span>
                {hasIncreased && (
                  <FaArrowUp
                    className="w-3 h-3 text-green-600 ml-1"
                    style={{
                      animation: isHovered ? 'pulse 1.5s infinite ease-in-out' : 'none',
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors gap-1.5">
                <span className="text-xs font-medium">Calculate</span>
                <PlusCircle className="w-3 h-3" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Hover tooltip */}
      {isHovered && (
        <div
          className={`absolute z-[10000] text-xs rounded-md py-1 px-2 left-0 -bottom-8 whitespace-nowrap shadow-lg dark:shadow-black/50 dark:bg-zinc-900 dark:border dark:border-white/10 ${
            hasIncreased
              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white dark:bg-none dark:text-green-400'
              : score > 0
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white dark:bg-none dark:text-blue-400'
                : 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white dark:bg-none dark:text-indigo-400'
          }`}
          style={{
            transform: 'translateX(-25%)',
            opacity: 0,
            animation: 'fadeIn 0.3s forwards',
            pointerEvents: 'none', // Prevent tooltip from interfering with other elements
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(5px) translateX(-25%); }
                to { opacity: 1; transform: translateY(0) translateX(-25%); }
              }
              @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.2); }
                100% { transform: scale(1); }
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          {getHoverText()}
        </div>
      )}
    </div>
  );
};

export default LeadScoreDisplay;
